import { useState, useCallback, useEffect } from "react";
import "./Timelinesection.css";

/* ═══════════════════════════════════════════════════════════
   DATA  — each event has a mediaKey matching your DB seed
═══════════════════════════════════════════════════════════ */
const EVENTS = [
    {
        year: "2024",
        phase: "Origins",
        status: "done",
        badge: "Founded",
        title: "Aviatricks Aero Labs Founded",
        body: "Aviatricks was established in Namakkal, Tamil Nadu with a vision to build indigenous, application-ready UAV systems. The focus was clear from day one — not just drones, but complete end-to-end solutions.",
        tags: ["Company Founded", "UAV Vision", "Namakkal · TN"],
        visual: "Founding · 2024",
        mediaKey: "timeline_founded",
        mediaSection: "timeline",
    },
    {
        year: "Early 2025",
        phase: "Research & Design",
        status: "done",
        badge: "R&D",
        title: "Concept & Airframe Design",
        body: "Initial research began across agriculture, mapping, and inspection domains. Custom drone architectures, payload configurations, and performance models were defined based on real-world use cases.",
        tags: ["Airframe Design", "Use Case Research", "System Architecture"],
        visual: "Research · Design Lab",
        mediaKey: "timeline_rnd",
        mediaSection: "timeline",
    },
    {
        year: "Mid 2025",
        phase: "Hardware Development",
        status: "done",
        badge: "Engineering",
        title: "Embedded Systems & Flight Hardware",
        body: "Development of core UAV hardware began — including flight controller R&D, PCB design, power systems, sensors, and communication modules. Focus was on reliability and modular architecture.",
        tags: ["Flight Controller", "PCB Design", "Sensors & Telemetry"],
        visual: "Hardware · Electronics Lab",
        mediaKey: "timeline_hardware",
        mediaSection: "timeline",
    },
    {
        year: "Late 2025",
        phase: "Software Integration",
        status: "done",
        badge: "Completed",
        title: "Firmware & Ground Control Systems",
        body: "Custom firmware, telemetry systems, and mission planning tools were developed alongside Drone Commander GCS. Integration between hardware and software layers enabled intelligent UAV control.",
        tags: ["Firmware", "GCS", "Mission Planning"],
        visual: "Software · GCS Interface",
        mediaKey: "timeline_software",
        mediaSection: "timeline",
    },
    {
        year: "Early 2026",
        phase: "Testing & Optimization",
        status: "active",
        badge: "Validation",
        title: "Flight Testing & Performance Tuning",
        body: "Extensive flight testing, calibration, and system validation are being conducted to ensure stability, safety, and real-world performance across multiple drone applications.",
        tags: ["Flight Testing", "Calibration", "Performance"],
        visual: "Testing · Flight Field",
        mediaKey: "timeline_testing",
        mediaSection: "timeline",
    },
    {
        year: "Mid 2026",
        phase: "Deployment",
        status: "active",
        badge: "Deployment",
        title: "Real-World Deployment",
        body: "Aviatricks drones will be deployed across agriculture, mapping, inspection, and enterprise use cases — delivering application-ready UAV solutions with integrated hardware and software.",
        tags: ["Agriculture", "Mapping", "Inspection"],
        visual: "Deploy · Field Ops",
        mediaKey: "timeline_deploy",
        mediaSection: "timeline",
    },
    {
        year: "Late 2026",
        phase: "Scale",
        status: "upcoming",
        badge: "Expansion",
        title: "Manufacturing & Platform Expansion",
        body: "Scaling in-house manufacturing, expanding product lines, and launching the full ecosystem — including AirspaceMap, Flyhub, and advanced autonomous capabilities.",
        tags: ["Manufacturing", "Platform Ecosystem", "Autonomy"],
        visual: "Scale · Production",
        mediaKey: "timeline_scale",
        mediaSection: "timeline",
    },
];

/* ═══════════════════════════════════════════════════════════
   CONFIG
═══════════════════════════════════════════════════════════ */
const API = process.env.REACT_APP_API_URL || '';

/* ═══════════════════════════════════════════════════════════
   HOOK — mirrors FlyhubHero fetch pattern exactly:
     GET /api/media/timeline
     Response: { timeline_founded: '/path/...', timeline_rnd: '...', ... }
   If a URL is relative it gets the API prefix prepended.
═══════════════════════════════════════════════════════════ */
function useTimelineMedia() {
    const [urls, setUrls] = useState({});  // { mediaKey: fullUrl }

    useEffect(() => {
        fetch(`${API}/api/media/timeline`)
            .then(r => r.json())
            .then(d => setUrls(d || {}))
            .catch(() => setUrls({}));
    }, []);

    // Resolve a mediaKey → absolute URL (same logic as FlyhubHero)
    function resolve(key) {
        if (!urls[key]) return null;
        return urls[key].startsWith('http') ? urls[key] : `${API}${urls[key]}`;
    }

    return { resolve };
}

/* ═══════════════════════════════════════════════════════════
   DRONE SVG  (fallback when image is absent / loading)
═══════════════════════════════════════════════════════════ */
function DroneSVG({ color = "#6D28D9", size = 76 }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 80 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <circle cx="40" cy="40" r="9" fill={color} opacity="0.9" />
            <circle cx="40" cy="40" r="4" fill="#fff" opacity="0.55" />
            <line x1="40" y1="40" x2="16" y2="16" stroke={color} strokeWidth="1.6" opacity="0.55" />
            <line x1="40" y1="40" x2="64" y2="16" stroke={color} strokeWidth="1.6" opacity="0.55" />
            <line x1="40" y1="40" x2="16" y2="64" stroke={color} strokeWidth="1.6" opacity="0.55" />
            <line x1="40" y1="40" x2="64" y2="64" stroke={color} strokeWidth="1.6" opacity="0.55" />
            <ellipse cx="13" cy="13" rx="10" ry="3.5" stroke={color} strokeWidth="1" opacity="0.40" transform="rotate(-45 13 13)" />
            <ellipse cx="67" cy="13" rx="10" ry="3.5" stroke={color} strokeWidth="1" opacity="0.40" transform="rotate(45 67 13)" />
            <ellipse cx="13" cy="67" rx="10" ry="3.5" stroke={color} strokeWidth="1" opacity="0.40" transform="rotate(45 13 67)" />
            <ellipse cx="67" cy="67" rx="10" ry="3.5" stroke={color} strokeWidth="1" opacity="0.40" transform="rotate(-45 67 67)" />
            <circle cx="13" cy="13" r="4" fill={color} opacity="0.75" />
            <circle cx="67" cy="13" r="4" fill={color} opacity="0.75" />
            <circle cx="13" cy="67" r="4" fill={color} opacity="0.75" />
            <circle cx="67" cy="67" r="4" fill={color} opacity="0.75" />
            <line x1="34" y1="49" x2="28" y2="58" stroke={color} strokeWidth="1.2" opacity="0.30" />
            <line x1="46" y1="49" x2="52" y2="58" stroke={color} strokeWidth="1.2" opacity="0.30" />
            <line x1="24" y1="58" x2="32" y2="58" stroke={color} strokeWidth="1.2" opacity="0.30" />
            <line x1="48" y1="58" x2="56" y2="58" stroke={color} strokeWidth="1.2" opacity="0.30" />
        </svg>
    );
}

/* ═══════════════════════════════════════════════════════════
   PANEL VISUAL  — shows DB image when available, drone SVG otherwise
═══════════════════════════════════════════════════════════ */
function PanelVisual({ ev, imageUrl }) {
    const color = ev.status === "upcoming" ? "#D97706" : "#6D28D9";
    const hasImage = Boolean(imageUrl);

    return (
        <div className="tl-panel-visual">
            {/* Dark BG (always present, sits behind image) */}
            <div className="tl-panel-visual-bg" />

            {/* Grid (hidden when image covers it) */}
            {!hasImage && <div className="tl-panel-visual-grid" />}

            {/* ── DATABASE IMAGE ── */}
            {hasImage && (
                <img
                    src={imageUrl}
                    alt={ev.title}
                    className="tl-panel-visual-img"
                    loading="lazy"
                    decoding="async"
                />
            )}

            {/* Colour glow overlay — always rendered for branded feel */}
            <div className="tl-panel-glow" />

            {/* Year watermark */}
            <div className="tl-panel-year-bg" aria-hidden="true">{ev.year}</div>

            {/* Fallback drone — shown only when there's no image */}
            {!hasImage && (
                <div className="tl-panel-drone">
                    <DroneSVG color={color} size={80} />
                </div>
            )}

            {/* Status pip */}
            <div className="tl-panel-pip" />

            {/* Corner label */}
            <div className="tl-panel-visual-label">{ev.visual}</div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════
   RIGHT PANEL
═══════════════════════════════════════════════════════════ */
function RightPanel({ ev, isVisible, imageUrl }) {
    const statusClass =
        ev.status === "active" ? "tl-panel--active"
            : ev.status === "upcoming" ? "tl-panel--upcoming"
                : "tl-panel--done";

    return (
        <div
            className={`tl-panel ${statusClass}${isVisible ? " tl-panel--visible" : ""}`}
            aria-hidden={!isVisible}
        >
            <PanelVisual ev={ev} imageUrl={imageUrl} />
            <div className="tl-panel-content">
                <div className="tl-panel-top">
                    <span className="tl-panel-badge">{ev.badge}</span>
                    <span className="tl-panel-year-label">{ev.year}</span>
                </div>
                <p className="tl-panel-phase">{ev.phase}</p>
                <h3 className="tl-panel-title">{ev.title}</h3>
                <hr className="tl-panel-rule" />
                <p className="tl-panel-body">{ev.body}</p>
                <div className="tl-panel-tags">
                    {ev.tags.map((tag) => (
                        <span key={tag} className="tl-panel-tag">{tag}</span>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════
   LEFT ROW
═══════════════════════════════════════════════════════════ */
function LeftRow({ ev, idx, isHovered, onEnter, onLeave }) {
    const num = String(idx + 1).padStart(2, "0");
    const statusClass =
        ev.status === "active" ? "tl-row--active-status"
            : ev.status === "upcoming" ? "tl-row--upcoming"
                : "tl-row--done";

    return (
        <div
            className={`tl-row ${statusClass}${isHovered ? " tl-hovered" : ""}`}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            onFocus={onEnter}
            onBlur={onLeave}
            role="button"
            tabIndex={0}
            aria-label={`${ev.year}: ${ev.title}`}
        >
            <span className="tl-row-num">{num}</span>
            <span className="tl-row-dot" aria-hidden="true" />
            <div className="tl-row-text">
                <div className="tl-row-meta">{ev.year} · {ev.phase}</div>
                <div className="tl-row-title">{ev.title}</div>
            </div>
            <span className="tl-row-arrow" aria-hidden="true">→</span>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════
   MOBILE ACCORDION ITEM
═══════════════════════════════════════════════════════════ */
function AccordionItem({ ev, idx, isOpen, onToggle, imageUrl }) {
    const num = String(idx + 1).padStart(2, "0");
    const statusClass =
        ev.status === "active" ? "tl-acc-item--active"
            : ev.status === "upcoming" ? "tl-acc-item--upcoming"
                : "tl-acc-item--done";
    const color = ev.status === "upcoming" ? "#D97706" : "#6D28D9";
    const hasImage = Boolean(imageUrl);

    return (
        <div className={`tl-acc-item ${statusClass}${isOpen ? " tl-acc-item--open" : ""}`}>
            <div
                className="tl-acc-trigger"
                onClick={onToggle}
                role="button"
                tabIndex={0}
                aria-expanded={isOpen}
                onKeyDown={(e) => e.key === "Enter" && onToggle()}
                aria-label={`${ev.year}: ${ev.title}`}
            >
                <span className="tl-acc-num">{num}</span>
                <span className="tl-acc-dot" aria-hidden="true" />
                <div className="tl-acc-text">
                    <div className="tl-acc-meta">{ev.year} · {ev.phase}</div>
                    <div className="tl-acc-title">{ev.title}</div>
                </div>
                <span className="tl-acc-chevron" aria-hidden="true">→</span>
            </div>

            <div className="tl-acc-body" aria-hidden={!isOpen}>
                <div className="tl-acc-content">

                    {/* Visual strip — DB image or drone fallback */}
                    <div className="tl-acc-visual">
                        {!hasImage && <div className="tl-acc-visual-grid" />}

                        {hasImage ? (
                            <img
                                src={imageUrl}
                                alt={ev.title}
                                className="tl-acc-visual-img"
                                loading="lazy"
                                decoding="async"
                            />
                        ) : (
                            <div className="tl-acc-visual-glow">
                                <DroneSVG color={color} size={52} />
                            </div>
                        )}

                        {/* Subtle colour overlay so corner label always readable */}
                        {hasImage && <div className="tl-acc-visual-overlay" />}

                        <div className="tl-acc-visual-label">{ev.visual}</div>
                    </div>

                    <span className="tl-acc-badge">{ev.badge}</span>
                    <p className="tl-acc-body-text">{ev.body}</p>
                    <div className="tl-acc-tags">
                        {ev.tags.map((tag) => (
                            <span key={tag} className="tl-acc-tag">{tag}</span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════
   SECTION
═══════════════════════════════════════════════════════════ */
export default function TimelineSection() {
    const [hoveredIdx, setHoveredIdx] = useState(5);
    const [openMobileIdx, setOpenMobileIdx] = useState(5);

    const { resolve } = useTimelineMedia();

    const handleEnter = useCallback((i) => setHoveredIdx(i), []);
    const handleLeave = useCallback(() => { }, []);
    const toggleMobile = useCallback((i) => {
        setOpenMobileIdx((prev) => (prev === i ? -1 : i));
    }, []);

    return (
        <section className="tl-section" aria-label="Company timeline">
            <div className="tl-inner">

                {/* Header */}
                <header className="tl-header">
                    <p className="tl-eyebrow">
                        <span className="tl-eyebrow-dot" aria-hidden="true" />
                        Our Journey
                    </p>
                    <h2 className="tl-h2">
                        From Concept<br />
                        to <em>Ecosystem.</em>
                    </h2>
                </header>

                {/* Desktop: Split layout */}
                <div className="tl-split" role="region" aria-label="Timeline milestones">
                    <div className="tl-left">
                        <div className="tl-left-head">
                            <p className="tl-left-label">
                                {EVENTS.length} Milestones &nbsp;·&nbsp; 2024 → 2026
                            </p>
                        </div>
                        {EVENTS.map((ev, i) => (
                            <LeftRow
                                key={ev.title}
                                ev={ev}
                                idx={i}
                                isHovered={hoveredIdx === i}
                                onEnter={() => handleEnter(i)}
                                onLeave={handleLeave}
                            />
                        ))}
                    </div>

                    <div className="tl-right" aria-live="polite">
                        {EVENTS.map((ev, i) => (
                            <RightPanel
                                key={ev.title}
                                ev={ev}
                                isVisible={hoveredIdx === i}
                                imageUrl={resolve(ev.mediaKey)}
                            />
                        ))}
                    </div>
                </div>

                {/* Mobile: Accordion */}
                <div className="tl-accordion" role="region" aria-label="Timeline milestones">
                    {EVENTS.map((ev, i) => (
                        <AccordionItem
                            key={ev.title}
                            ev={ev}
                            idx={i}
                            isOpen={openMobileIdx === i}
                            onToggle={() => toggleMobile(i)}
                            imageUrl={resolve(ev.mediaKey)}
                        />
                    ))}
                </div>

                {/* Footer */}
                <footer className="tl-footer">
                    <p className="tl-footer-note">
                        Every milestone above represents a deliberate step toward building the most complete
                        drone technology platform in India — from the ground station to the airspace map.
                    </p>
                    <div className="tl-footer-loc" aria-label="Namakkal, Tamil Nadu, India">
                        <span className="tl-footer-loc-dot" aria-hidden="true" />
                        Namakkal · Tamil Nadu · India
                    </div>
                </footer>

            </div>
        </section>
    );
}